import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { Card, Nav, Tab, Form } from "@themesberg/react-bootstrap";

import { userLevelNetworkData, userProfileData, userMyReferralList } from "../../../pages/api";
import NetworkSummary from "../../../components/network/NetworkSummary";
import DirectReferralsTab from "../../../components/network/DirectReferralsTab";
import LevelWiseNetworkTab from "../../../components/network/LevelWiseNetworkTab";
import TreeViewTab from "../../../components/network/TreeViewTab";
import {
  NETWORK_EXPLANATION,
  mapLevelItem,
  mapTreeItem,
  countPurchasedInList,
  mapDirectReferral,
} from "../../../components/network/networkHelpers";
import { getStoredUser } from "../../../components/dashboard/dashboardHelpers";

const TAB_KEYS = {
  direct: "direct",
  levels: "levels",
  tree: "tree",
};

export default function UserLevelNetworkPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || TAB_KEYS.direct;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState({ direct_referrals_count: 0, total_team_count: 0 });
  const [levels, setLevels] = useState([]);
  const [treeFlat, setTreeFlat] = useState([]);
  const [totalCommission, setTotalCommission] = useState(0);
  const [purchasedDirectCount, setPurchasedDirectCount] = useState(0);
  const [focusUserId, setFocusUserId] = useState(null);
  const [levelFilter, setLevelFilter] = useState("");
  const [purchaseFilter, setPurchaseFilter] = useState("");

  const storedUser = getStoredUser();

  const fetchNetwork = async (levelValue = "") => {
    try {
      setLoading(true);
      const params = {};
      if (levelValue) params.level = levelValue;

      const [networkRes, profileRes, referralsRes] = await Promise.allSettled([
        userLevelNetworkData(params),
        userProfileData(),
        userMyReferralList({ page: 1, per_page: 50 }),
      ]);

      if (networkRes.status === "fulfilled") {
        const data = networkRes.value?.data?.data || {};
        setSummary({
          direct_referrals_count: Number(data?.summary?.direct_referrals_count || 0),
          total_team_count: Number(data?.summary?.total_team_count || 0),
        });
        const levelList = Array.isArray(data?.levels) ? data.levels.map(mapLevelItem) : [];
        setLevels(levelList);
        setTotalCommission(levelList.reduce((sum, l) => sum + l.commissionEarned, 0));
        setTreeFlat(
          Array.isArray(data?.tree)
            ? data.tree.map((item, index) => mapTreeItem(item, index))
            : []
        );
      }

      if (profileRes.status === "fulfilled") {
        setProfile(profileRes.value?.data?.data || profileRes.value?.data || {});
      }

      if (referralsRes.status === "fulfilled") {
        const refData = referralsRes.value?.data?.data || referralsRes.value?.data || {};
        const refs = (Array.isArray(refData?.referrals) ? refData.referrals : []).map(mapDirectReferral);
        setPurchasedDirectCount(countPurchasedInList(refs));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load network data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNetwork(levelFilter);
  }, [levelFilter]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && Object.values(TAB_KEYS).includes(tab)) {
      setActiveTab(tab);
    }
    const focus = searchParams.get("focus");
    if (focus) {
      setFocusUserId(Number(focus));
      setActiveTab(TAB_KEYS.tree);
    }
  }, [searchParams]);

  const handleTabSelect = (key) => {
    setActiveTab(key);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", key);
      if (key !== TAB_KEYS.tree) next.delete("focus");
      return next;
    });
  };

  const handleViewDownline = (userId) => {
    setFocusUserId(userId);
    setActiveTab(TAB_KEYS.tree);
    setSearchParams({ tab: TAB_KEYS.tree, focus: String(userId) });
  };

  const rootUser = useMemo(
    () => ({
      name: profile?.name || storedUser?.name || "You",
      userId: profile?.custom_user_id || storedUser?.custom_user_id || storedUser?.id,
    }),
    [profile, storedUser]
  );

  const referralCode = profile?.referral_number || storedUser?.referral_number || "";

  return (
    <div className="network-page">
      <div className="mb-3">
        <h2 className="h4 mb-1" style={{ color: "#111827" }}>My Network</h2>
        <p className="text-muted mb-0 small">Understand your referrals and downline at a glance.</p>
      </div>

      <div className="network-info-box mb-4">{NETWORK_EXPLANATION}</div>

      <NetworkSummary
        loading={loading}
        userId={profile?.custom_user_id || storedUser?.custom_user_id || storedUser?.id || "—"}
        referralCode={referralCode}
        directCount={summary.direct_referrals_count}
        totalDownline={summary.total_team_count}
        purchasedCount={purchasedDirectCount}
        totalCommission={totalCommission}
      />

      <div className="network-filter-bar mb-3">
        <Form.Select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          style={{ maxWidth: 180 }}
        >
          <option value="">All Levels</option>
          {Array.from({ length: 9 }, (_, i) => (
            <option key={i + 1} value={String(i + 1)}>
              Level {i + 1}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          value={purchaseFilter}
          onChange={(e) => setPurchaseFilter(e.target.value)}
          style={{ maxWidth: 200 }}
        >
          <option value="">All Purchase Status</option>
          <option value="purchased">Purchased Only</option>
          <option value="not_purchased">Not Purchased</option>
        </Form.Select>
        <span className="text-muted small align-self-center">
          <FontAwesomeIcon icon={faFilter} className="me-1" />
          Filters apply to list views
        </span>
      </div>

      <Card className="dashboard-section border-0">
        <Card.Body className="p-3 p-md-4">
          <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
            <Nav variant="pills" className="network-tabs mb-4 flex-wrap gap-2">
              <Nav.Item>
                <Nav.Link eventKey={TAB_KEYS.direct}>Direct Referrals</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={TAB_KEYS.levels}>Level-wise Network</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={TAB_KEYS.tree}>Tree View</Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey={TAB_KEYS.direct}>
                <DirectReferralsTab
                  onViewDownline={handleViewDownline}
                  purchaseFilter={purchaseFilter}
                  referralCode={referralCode}
                />
              </Tab.Pane>
              <Tab.Pane eventKey={TAB_KEYS.levels}>
                <LevelWiseNetworkTab levels={levels} treeFlat={treeFlat} loading={loading} />
              </Tab.Pane>
              <Tab.Pane eventKey={TAB_KEYS.tree}>
                <TreeViewTab
                  treeFlat={treeFlat}
                  rootUser={rootUser}
                  loading={loading}
                  focusUserId={focusUserId}
                />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>
    </div>
  );
}
